import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { dashboardContentService } from '../../../services/dashboardContentService';

const PostOfWeekEditModal = ({ post, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: post?.title || '',
    caption: post?.caption || '',
    imageAlt: post?.imageAlt || '',
    platform: post?.platform || 'LinkedIn',
    hashtags: post?.hashtags?.join(', ') || ''
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(post?.image || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (!selectedFile) return;

    // Validate file size (50MB max)
    if (selectedFile?.size > 52428800) {
      setError('File size must be less than 50MB');
      return;
    }

    // Validate file type
    if (!selectedFile?.type?.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setFile(selectedFile);
    setError('');

    // Generate preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader?.result);
    };
    reader?.readAsDataURL(selectedFile);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData?.title?.trim() || !formData?.caption?.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (!post && !file) {
      setError('Please select an image');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const postData = {
        title: formData?.title,
        caption: formData?.caption,
        imageAlt: formData?.imageAlt || formData?.title,
        platform: formData?.platform,
        hashtags: formData?.hashtags?.split(',')?.map(tag => tag?.trim())?.filter(Boolean) || []
      };

      if (post?.id) {
        await dashboardContentService?.updatePostOfWeek(post?.id, postData, file);
      } else {
        await dashboardContentService?.createPostOfWeek(postData, file);
      }

      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {post ? 'Edit' : 'Create'} Post of the Week
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Title *
            </label>
            <Input
              value={formData?.title}
              onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
              placeholder="Enter post title"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Caption *
            </label>
            <textarea
              value={formData?.caption}
              onChange={(e) => setFormData({ ...formData, caption: e?.target?.value })}
              placeholder="Enter post caption"
              rows={6}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Image {!post && '*'}
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer">
              <input
                type="file"
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
                id="post-image-upload"
              />
              <label htmlFor="post-image-upload" className="cursor-pointer">
                {preview ? (
                  <img src={preview} alt="Preview" className="max-h-48 mx-auto mb-3 rounded" />
                ) : (
                  <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-3" />
                )}
                <p className="text-foreground font-medium mb-1">
                  {file ? file?.name : preview ? 'Click to change image' : 'Click to upload image'}
                </p>
                <p className="text-sm text-muted-foreground">
                  Images only (max 50MB)
                </p>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Image Alt Text
            </label>
            <Input
              value={formData?.imageAlt}
              onChange={(e) => setFormData({ ...formData, imageAlt: e?.target?.value })}
              placeholder="Describe the image for accessibility"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Platform *
            </label>
            <select
              value={formData?.platform}
              onChange={(e) => setFormData({ ...formData, platform: e?.target?.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              required
            >
              <option value="LinkedIn">LinkedIn</option>
              <option value="Instagram">Instagram</option>
              <option value="Facebook">Facebook</option>
              <option value="Twitter">Twitter</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Hashtags (comma-separated)
            </label>
            <Input
              value={formData?.hashtags}
              onChange={(e) => setFormData({ ...formData, hashtags: e?.target?.value })}
              placeholder="#SurgicalPrecision, #MedicalInnovation, #PatientCare"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive rounded-lg">
              <Icon name="AlertCircle" size={20} className="text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <div className="flex items-center gap-3 pt-4">
            <Button
              type="submit"
              loading={saving}
              disabled={saving}
              iconName="Save"
              iconPosition="left"
              className="flex-1"
            >
              {saving ? 'Saving...' : post ? 'Update Post' : 'Create Post'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostOfWeekEditModal;